import { type CollectibleAuctionOneRefined } from '@capsicum/common';
import { Timer, useContainerDimensions, useIdParam, useRouter } from '@capsicum/common-web';
import { auctionsUtils, useAuctionsById } from '@capsicum/features/fanshop/shared';
import { SkeletonEventAuctions } from '@capsicum/features/fanshop/web';
import { useCallback, useRef } from 'react';
import { FixedSizeGrid as Grid, type GridChildComponentProps } from 'react-window';

import { AuctionCard } from '../../../ui/auction-card/AuctionCard';

import * as S from './Event.styled';
import { useColWidth } from './useColWidth';

type ProductProps = {
	params: { id?: string };
};

export const Event = ({ params }: ProductProps) => {
	const router = useRouter();
	const { getAwayTeam, getHomeTeam } = auctionsUtils();

	const { id: eventId } = useIdParam(params, '/fan-shop/rewards');

	const { auctionsArray, eventClosed, events, isLoading } = useAuctionsById(eventId || '', 50);

	const homeTeam = getHomeTeam(events[0] || {});
	const awayTeam = getAwayTeam(events[0] || {});

	const colWidth: number = useColWidth();

	//#region grid height and width claculation
	const componentRef = useRef(null);
	const { width: containerWidth } = useContainerDimensions(componentRef);

	const colCount = Math.floor(containerWidth / colWidth);
	const rowCount = colCount ? Math.ceil(auctionsArray.length / colCount) : 0;

	const gridHeight = S.ROW_HEIGHT * rowCount;
	const gridWidth = colWidth * colCount;
	//#endregion

	const RenderItem = useCallback(
		({ columnIndex, rowIndex, style }: GridChildComponentProps<CollectibleAuctionOneRefined>) => {
			const index = rowIndex * colCount + columnIndex;
			const auction = auctionsArray[index] as CollectibleAuctionOneRefined;

			if (!auction) return null;

			return (
				<div key={`event-page-auction-card-${auction.id}`} style={style}>
					<S.AuctionWrapper>
						<AuctionCard
							auction={auction}
							key={auction.id}
							testID={`event-page-auction-card-${auction.id}`}
							onClick={() => router.push(`/fan-shop/rewards/auction/${auction.id}`)}
						/>
					</S.AuctionWrapper>
				</div>
			);
		},
		[auctionsArray, colCount, router]
	);

	return (
		<S.Container ref={componentRef}>
			<S.Content $maxWidth={gridWidth}>
				{isLoading ? (
					<SkeletonEventAuctions />
				) : (
					<>
						<S.TitleWrapper>
							{homeTeam && awayTeam && (
								<S.TitleContainer>
									<S.Title>{`${homeTeam} -`}</S.Title>
									<S.Subtitle>{awayTeam}</S.Subtitle>
								</S.TitleContainer>
							)}
							{eventClosed && <Timer closedLabel expirationDate={undefined} />}
						</S.TitleWrapper>
						{/*
                    TODO: The Grid types coming from react-window show a TS error that
                    the render method is not compatible with JSXElement.
                    Ignoring this error for now
                */}
						{/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
						{/* @ts-ignore */}
						<Grid<CollectibleAuctionOneRefined>
							columnCount={colCount}
							columnWidth={colWidth}
							height={gridHeight}
							rowCount={rowCount}
							rowHeight={S.ROW_HEIGHT}
							style={{ margin: 'auto' }}
							width={gridWidth}
						>
							{RenderItem}
						</Grid>
					</>
				)}
			</S.Content>
		</S.Container>
	);
};
